import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowBackRounded } from "@mui/icons-material";
import { Dialog } from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";
import "../game/game.css";
import { computeTeams, rankTeams, teamOfQuestion } from "../game/logic";
import useGameStore from "../game/store";

const LETTERS = ["A", "B", "C", "D"];
const LOW_SECONDS = 5;
// Amarelo, normal, amarelo, normal (4 tempos) antes de revelar o resultado
const BLINK_MS = 1200;

export default function GamePlayPage() {
  const navigate = useNavigate();
  const { session, config, answer, next, endSession } = useGameStore();

  const seconds = session?.seconds ?? 0;
  const index = session?.index ?? 0;
  const answered = session?.selected !== null && session?.selected !== undefined;
  const [remaining, setRemaining] = useState(seconds);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Opção escolhida que está a piscar; ligada à pergunta para nunca "vazar" para a seguinte
  const [picked, setPicked] = useState<{ index: number; option: number } | null>(null);
  const blinkTimer = useRef<number>();
  const picking = !answered && picked?.index === index;
  const rootRef = useRef<HTMLDivElement>(null);
  const remainingRef = useRef(seconds);
  const answerRef = useRef(answer);
  answerRef.current = answer;

  // Nova pergunta: o cronómetro volta ao tempo total
  useEffect(() => {
    remainingRef.current = seconds;
    setRemaining(seconds);
  }, [index, seconds]);

  // Cronómetro por pergunta; ao chegar a 0 conta como erro automático (-1).
  // Fica em pausa enquanto o modal de cancelar está aberto e retoma do tempo que restava.
  useEffect(() => {
    if (!session || answered || picking || confirmOpen) return;
    const end = Date.now() + remainingRef.current * 1000;
    const id = window.setInterval(() => {
      const left = Math.max(0, (end - Date.now()) / 1000);
      remainingRef.current = left;
      setRemaining(left);
      if (left <= 0) {
        window.clearInterval(id);
        answerRef.current(-1);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [index, answered, picking, seconds, !!session, confirmOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const question = session?.questions[index];
  const total = session?.questions.length ?? 0;
  const isLast = index >= total - 1;

  const cancelGame = () => {
    setConfirmOpen(false);
    endSession();
    navigate("/", { replace: true });
  };

  const goNext = useCallback(() => {
    if (!session || session.selected === null) return;
    if (isLast) {
      navigate("/jogo/resultado", { replace: true });
    } else {
      next();
    }
  }, [session, isLast, navigate, next]);

  // Escolher: a opção pisca e só depois a resposta é registada e revelada
  const choose = useCallback(
    (option: number) => {
      if (answered || picking) return;
      setPicked({ index, option });
      blinkTimer.current = window.setTimeout(() => answerRef.current(option), BLINK_MS);
    },
    [answered, picking, index],
  );

  useEffect(() => () => window.clearTimeout(blinkTimer.current), []);

  // Atalhos: A–D ou 1–4 escolhem a alternativa; N, P ou Enter avançam para a próxima equipe
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (confirmOpen || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      const key = e.key.toUpperCase();

      const optionIndex =
        LETTERS.indexOf(key) >= 0 ? LETTERS.indexOf(key) : ["1", "2", "3", "4"].indexOf(key);
      if (optionIndex >= 0) {
        if (!answered && !picking && question && optionIndex < question.options.length) {
          choose(optionIndex);
        }
        return;
      }

      if ((key === "N" || key === "P" || key === "ENTER") && answered) {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [answered, picking, question, choose, goNext, confirmOpen]);

  const teams = useMemo(
    () => (session ? computeTeams(session.teamNames, session.results) : []),
    [session],
  );

  if (!session || !question) {
    return <Navigate to="/" replace />;
  }

  const turn = teamOfQuestion(index, session.teamNames.length);
  const current = teams[turn];
  const ranking = rankTeams(teams);
  const shownSeconds = Math.ceil(remaining);
  const low = !answered && remaining <= LOW_SECONDS;
  const timedOut = session.selected === -1;
  const correctIndex = question.options.findIndex((o) => o.correct);
  const lastResult = answered ? session.results[session.results.length - 1] : null;

  let feedback = "";
  if (answered) {
    const right = `${LETTERS[correctIndex]} — ${question.options[correctIndex].res}`;
    feedback = lastResult
      ? `Resposta correta! +1 ponto para ${current.name}.`
      : timedOut
        ? `Tempo esgotado! A resposta certa era ${right}`
        : `Resposta incorreta. A resposta certa era ${right}`;
  }

  return (
    <div className="qz-root" ref={rootRef}>
      <div className="qz-play">
        <div className="qz-play-main">
          <div className="qz-top">
            <button
              type="button"
              className="qz-back"
              aria-label="Voltar e cancelar o jogo"
              onClick={() => setConfirmOpen(true)}
            >
              <ArrowBackRounded fontSize="small" />
              Voltar
            </button>
            <div className="qz-strip" aria-live="polite">
              <div className="qz-strip-turn">
                <span className="qz-dot qz-dot-sm" style={{ background: current.color }} />
                <span>Vez de: {current.name}</span>
              </div>
              <span className="qz-strip-count">
                Pergunta {index + 1} de {total}
              </span>
            </div>
          </div>

          <section className="qz-card qz-question-card" aria-label="Pergunta">
            <div className="qz-card-head">
              <div className="qz-label">Pergunta</div>
              <button
                type="button"
                className="qz-btn qz-btn-primary qz-btn-sm"
                disabled={!answered}
                onClick={goNext}
              >
                {isLast ? "Ver resultado" : "Próxima equipe"}
              </button>
            </div>
            <div className="qz-q-wrap">
              <h2 className="qz-q-text qz-serif" style={{ fontSize: config.fontQuestion }}>
                {question.questao}
              </h2>
            </div>

            <div className="qz-card-head qz-options-head">
              <div className="qz-label">Alternativas</div>
              <p
                className={`qz-feedback ${answered ? (lastResult ? "is-ok" : "is-bad") : ""}`}
                role="status"
              >
                {feedback}
              </p>
            </div>
            <div className="qz-options" role="group" aria-label="Alternativas">
              {question.options.map((option, i) => {
                const isCorrect = answered && option.correct;
                const isWrong = answered && session.selected === i && !option.correct;
                const isBlinking = picking && picked?.option === i;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={answered || picking}
                    className={`qz-option ${isCorrect ? "is-correct" : ""} ${isWrong ? "is-wrong" : ""} ${
                      isBlinking ? "is-blinking" : ""
                    } ${answered && !isCorrect && !isWrong ? "is-dim" : ""}`}
                    style={{
                      fontSize: config.fontOption,
                      ...(isBlinking ? { animationDuration: `${BLINK_MS}ms` } : {}),
                    }}
                    onClick={() => choose(i)}
                  >
                    <span className="qz-option-letter" aria-hidden>{LETTERS[i]}</span>
                    <span className="qz-option-text">{option.res}</span>
                  </button>
                );
              })}
            </div>


          </section>
        </div>

        <aside className="qz-card qz-side" aria-label="Placar">
          <div className="qz-label">Equipes</div>
          <ul className="qz-team-list">
            {ranking.map((team) => (
              <li
                key={team.index}
                className={`qz-team-item ${team.index === turn ? "is-active" : ""}`}
                style={{ ["--team" as string]: team.color }}
                aria-current={team.index === turn ? "true" : undefined}
              >
                <span className="qz-dot qz-dot-sm" style={{ background: team.color }} />
                <span className="qz-team-name">{team.name}</span>
                <span className="qz-team-score">{team.score}</span>
              </li>
            ))}
          </ul>

          <hr className="qz-hr" />

          <div className="qz-label">{current.name} nesta partida</div>
          <div className="qz-stats">
            <div className="qz-stat is-ok">
              <strong>{current.correct}</strong>
              <span>Acertos</span>
            </div>
            <div className="qz-stat is-bad">
              <strong>{current.wrong}</strong>
              <span>Erros</span>
            </div>
          </div>

          <hr className="qz-hr" />

          <div className="qz-label">Cronômetro</div>
          <div className={`qz-timer-value ${low ? "is-low" : ""}`} aria-label={`${shownSeconds} segundos restantes`}>
            {answered ? "—" : `${shownSeconds}s`}
          </div>
          <div className="qz-bar" aria-hidden>
            <div
              className={low ? "is-low" : ""}
              style={{ width: `${answered ? 0 : (remaining / seconds) * 100}%` }}
            />
          </div>
        </aside>
      </div>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        container={rootRef.current}
        aria-labelledby="cancel-title"
        aria-describedby="cancel-desc"
        PaperProps={{
          sx: {
            width: "min(420px, calc(100vw - 32px))",
            m: 0,
            p: "28px",
            borderRadius: "22px",
            border: "1px solid #E2E4E0",
            boxShadow: "0 30px 80px -20px rgba(27,24,48,0.35)",
            fontFamily: "inherit",
          },
        }}
      >
        <h2 id="cancel-title" className="qz-modal-title">Cancelar o jogo?</h2>
        <p id="cancel-desc" className="qz-modal-text">
          A partida em curso será perdida e voltará à tela de configuração.
        </p>
        <div className="qz-modal-actions">
          <button
            type="button"
            className="qz-btn qz-btn-outline"
            autoFocus
            onClick={() => setConfirmOpen(false)}
          >
            Continuar jogo
          </button>
          <button type="button" className="qz-btn qz-btn-danger" onClick={cancelGame}>
            Cancelar jogo
          </button>
        </div>
      </Dialog>
    </div>
  );
}
